import React from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Grid, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Tooltip, FormControlLabel, Switch } from '@mui/material';
import { Search } from '@mui/icons-material';
import { Button, Dialog, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { onTextBoxChange, onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { withRights } from 'providers/RightsConsumer';
import moment from 'moment';
import assetHierarchyFunctions from '../../hierarchy/assetHierarchyFunctions';
import AssetSearchComponent from 'features/asset/hierarchy/components/AssetSearchComponent';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { ConfiguratorAssetImage } from './AssetConfiguratorStyle';
import { BoxActionsWrapper } from 'components/common/CommonStyles';
import PaginationWrapper from 'components/common/PaginationWrapper';
import { getDateValue } from 'utils/functions';

const AssetConfiguratorComponent = ({
  t,
  vehicleEquipments,
  availableOptions,
  assetInfo,
  maxMonths,
  financedAssetWrite,
  onCloseModal,
  onSelectedChange,
  onPropertyChange,
  onSave,
  onFilterChange,
  nameFilter,
  showModal,
  isDirty,
  loading,
  totalCount,
  pageSize,
  page,
  onChangePage,
  rights,
  assetImage,
  hierarchyLevels,
  hierarchy
}) => {
  const onSelectedChangeInternal = (onChange, id) => event => {
    onChange(event.target.checked, id);
  };

  const isValidDate = current => {
    if (current) {
      var calendarMaximumMonths = moment().subtract(maxMonths, 'months');
      const currentMonth = moment();
      return current.isBefore(currentMonth) && current.isAfter(calendarMaximumMonths);
    } else {
      return true;
    }
  };

  const { getHierarchyItem } = assetHierarchyFunctions(hierarchyLevels, hierarchy, onPropertyChange, rights);

  return (
    <Dialog
      open={showModal}
      aria-labelledby='equipments-dialog-title'
      maxWidth={'lg'}
      fullWidth
      onClose={onCloseModal}
      title={t('OfferEdit.Asset.Equipments.Title')}
      content={
        vehicleEquipments == null ? (
          <WrappedFakeText lines={7} my={1} />
        ) : (
          <Grid container>
            <Grid item xs={12}></Grid>
            <Grid item xs={12} sm={12}>
              <Grid container spacing={4}>
                <Grid item xs={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        id={'offerAssetCard_secondHand_isSecondHand'}
                        checked={assetInfo.isSecondHand || false}
                        onChange={onCheckBoxChange(onPropertyChange('isSecondHand'))}
                        disabled={rights.readOnlyFull || !financedAssetWrite}
                      />
                    }
                    label={t('OfferEdit.Asset.Info.SecondHand')}
                  />
                </Grid>
                <Grid item xs={4} id={'offerAssetCard_secondHand_buildYearAndMonth'}>
                  <DateTime
                    value={assetInfo.buildYearAndMonth}
                    label={t('OfferEdit.Asset.Info.BuildYearAndMonth')}
                    onChange={onPropertyChange('buildYearAndMonth')}
                    error={Boolean(assetInfo.buildYearAndMonthError)}
                    helperText={assetInfo.buildYearAndMonthError}
                    inputFormat='yyyy-MM'
                    closeOnSelect={true}
                    disabled={rights.readOnlyFull || !financedAssetWrite}
                    isValidDate={isValidDate}
                    required
                  />
                </Grid>
                <AssetSearchComponent
                  onPropertyChange={onPropertyChange}
                  buildYearAndMonth={getDateValue(assetInfo.buildYearAndMonth)}
                  configurator={true}
                  configuratorHierarchy={hierarchyLevels}
                ></AssetSearchComponent>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={4} sm={4}>
                <Grid container>
                  <Grid item xs={12}></Grid>
                  <Grid item xs={12}>
                    <TextField
                      id={'offerAssetCard_equipments_filter'}
                      fullWidth
                      value={nameFilter || ''}
                      name='nameFilter'
                      label={t('OfferEdit.Asset.Equipments.Filter')}
                      onChange={onTextBoxChange(onFilterChange)}
                      startAdornment={<Search />}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={2} sm={2}></Grid>

              <Grid item xs={6} sm={6}>
                <Grid container justifyContent='center'>
                  {assetImage && <ConfiguratorAssetImage src={assetImage} alt='' />}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12} md={12}>
              {loading ? (
                <WrappedFakeText lines={7} my={1} />
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>{t('OfferEdit.Asset.Equipments.Name')}</TableCell>
                      <TableCell>{t('OfferEdit.Asset.Equipments.Type')}</TableCell>
                      <TableCell>{t('OfferEdit.Asset.Equipments.Price')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableOptions.map((option, idx) => {
                      return (
                        <TableRow key={option.code}>
                          <TableCell>
                            <Checkbox
                              id={'offerAssetCard_equipments_' + idx + '_selected'}
                              checked={option.selected}
                              onChange={onSelectedChangeInternal(onSelectedChange, option.id)}
                            />
                          </TableCell>
                          <Tooltip title={option.name}>
                            <TableCell id={'offerAssetCard_equipments_' + idx + '_name'}>{option.name}</TableCell>
                          </Tooltip>
                          <TableCell>{option.typeName}</TableCell>
                          <TableCell id={'offerAssetCard_equipments_' + idx + '_price'}>{option.price}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              <PaginationWrapper
                loading={loading}
                count={totalCount}
                pageSize={pageSize}
                page={page}
                onPageChange={onChangePage}
                onRefresh={() => {}}
                rowsPerPageOptions={[]}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Grid container spacing={1}>
                <Grid item xs={6}></Grid>
                <Grid item xs={3}>
                  <TextField
                    id={'offerAssetCard_equipments_assetBasePrice'}
                    disabled={true}
                    fullWidth
                    isNumeric
                    value={vehicleEquipments.basePrice || 0}
                    name='basePrice'
                    label={t('OfferEdit.Asset.Equipments.BasePrice')}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    id={'offerAssetCard_equipments_assetFinalPrice'}
                    disabled={true}
                    fullWidth
                    isNumeric
                    value={vehicleEquipments.finalPrice || 0}
                    name='finalPrice'
                    label={t('OfferEdit.Asset.Equipments.FinalPrice')}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )
      }
      actions={
        <BoxActionsWrapper forDialog>
          <Button id={'offerAssetCard_equipments_saveBtn'} onClick={onSave} disabled={!isDirty || getHierarchyItem()?.name === null}>
            {t('Save')}
          </Button>
          <Button id={'offerAssetCard_equipments_closeBtn'} onClick={onCloseModal} color='primary'>
            {t('Close')}
          </Button>
        </BoxActionsWrapper>
      }
    />
  );
};

AssetConfiguratorComponent.propTypes = {
  t: PropTypes.func.isRequired,
  vehicleEquipments: PropTypes.object,
  availableOptions: PropTypes.array,
  maxMonths: PropTypes.number,
  assetTypes: PropTypes.array.isRequired,
  buildYearAndMonthError: PropTypes.string,
  financedAssetWrite: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onSelectedChange: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  nameFilter: PropTypes.string,
  showModal: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  totalCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  assetImage: PropTypes.string,
  hierarchyLevels: PropTypes.array.isRequired,
  hierarchy: PropTypes.object.isRequired,
  assetInfo: PropTypes.object.isRequired,
  rights: PropTypes.object.isRequired
};

export default compose(withTranslation())(withRights(AssetConfiguratorComponent));
